import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, CircleCheck, Circle, Copy, MessagesSquare, Plus, Share, SlidersHorizontal, Trash2 } from "lucide-react";
import {
  RelayInviteCode, TokenCount, Welcome, botShares, createInvite, followMainCredentials, insights, profileTitle, removeGuest,
  revokeInvite, setMonthlyLimit, HermesError, type BotShares, type ProfileDetail, type ProfileSummary, type ShareGuest, type ShareInvite,
} from "@timibot/core";
import { useApp } from "../../context";
import { useObserve } from "../../state/observable";
import { copyText } from "../../ui/clipboard";
import { BotAvatar, Dialog, Menu, Sheet, Spinner, useLongPress, useMenu } from "../../ui/components";
import { ModelPickerSheet } from "../chat/ModelPicker";
import "./bots.css";

type Page = { type: "list" } | { type: "detail"; name: string } | { type: "edit"; name: string };

/**
 * 机器人（高级功能，从设置或标题菜单进入，对应 iOS 的 BotsView）：每个 Bot 是一个 Hermes profile。
 * 以“分享给朋友”为主：点进一个 Bot 直接是邀请、用量和访客；改名字、人设、模型放在“编辑”里。
 */
export function BotsView({ open, onClose }: { open: boolean; onClose: () => void }) {
  const app = useApp();
  useObserve(app.profiles);
  const [stack, setStack] = useState<Page[]>([{ type: "list" }]);
  const [showCreate, setShowCreate] = useState(false);
  const page = stack[stack.length - 1];
  const push = (p: Page) => setStack((s) => [...s, p]);
  const pop = () => setStack((s) => s.slice(0, -1));
  useEffect(() => { if (open) { setStack([{ type: "list" }]); void app.profiles.refresh(app.api); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const profile = page.type !== "list" ? app.profiles.profiles.find((p) => p.name === page.name) : undefined;
  const title = page.type === "list" ? "机器人" : page.type === "edit" ? "编辑" : profile ? profileTitle(profile) : "";

  return (
    <Sheet open={open} onClose={onClose} title={title}
      leading={stack.length > 1 ? <button className="sheet-done row" onClick={pop}><ChevronLeft size={18} />返回</button> : undefined}
      trailing={page.type === "list" && !app.isGuest ? <button className="glass-button" style={{ width: 38, height: 38 }} aria-label="新建机器人" onClick={() => setShowCreate(true)}><Plus size={18} /></button> : undefined}>
      {page.type === "list" && <BotList onOpen={(p) => push({ type: "detail", name: p.name })} onClose={onClose} />}
      {page.type === "detail" && profile && <BotDetail profile={profile} onEdit={() => push({ type: "edit", name: profile.name })} onClose={onClose} onDeleted={pop} />}
      {page.type === "edit" && profile && <BotEdit profile={profile} />}
      <CreateBot open={showCreate} onClose={() => setShowCreate(false)} />
    </Sheet>
  );
}

function BotList({ onOpen, onClose }: { onOpen: (p: ProfileSummary) => void; onClose: () => void }) {
  const app = useApp();
  const menu = useMenu();
  const [target, setTarget] = useState<ProfileSummary>();
  const [pendingDelete, setPendingDelete] = useState<ProfileSummary>();
  useEffect(() => { for (const p of app.profiles.profiles) void app.profiles.loadAvatar(p, app.api); }, [app.profiles.profiles.length]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="bot-list">
      {app.profiles.profiles.map((p) => (
        <BotRow key={p.name} profile={p} current={app.profiles.apiName(p) === app.selectedProfile} onOpen={() => onOpen(p)}
          onMenu={(pos) => { setTarget(p); menu.show(pos); }} />
      ))}
      {app.profiles.isLoading && !app.profiles.profiles.length && <div className="center-pad"><Spinner /></div>}
      <Menu open={menu.open} anchor={menu.anchor} onClose={menu.close} align="start" width={170} items={target ? [
        { label: "对话", icon: <MessagesSquare size={16} />, onSelect: () => { app.selectProfile(app.profiles.apiName(target)); onClose(); } },
        ...(!app.isGuest && !target.is_default ? [{ label: "删除", icon: <Trash2 size={16} />, destructive: true, onSelect: () => setPendingDelete(target) }] : []),
      ] : []} />
      <DeleteBotDialog target={pendingDelete} onClose={() => setPendingDelete(undefined)} />
    </div>
  );
}

function BotRow({ profile, current, onOpen, onMenu }: { profile: ProfileSummary; current: boolean; onOpen: () => void; onMenu: (pos: { x: number; y: number }) => void }) {
  const app = useApp();
  const press = useLongPress(onMenu);
  const title = profileTitle(profile);
  return (
    <button className="bot-row" onClick={onOpen} {...press}>
      <BotAvatar title={title} image={app.profiles.avatars.get(profile.name)} size={40} />
      <span className="col" style={{ flex: 1, minWidth: 0, gap: 2 }}>
        <span className="row" style={{ gap: 6 }}>
          <span className="t-body medium nowrap">{title}</span>
          {current && <span className="current-tag t-caption2 semibold">当前</span>}
        </span>
        <span className="t-caption t-2 nowrap">{profile.description || profile.model || "默认模型"}</span>
      </span>
      <ChevronRight size={14} className="t-3" />
    </button>
  );
}

/** 删除 Bot 的确认。删除是 Hermes 原生操作，目录整个删掉，不能恢复 */
function DeleteBotDialog({ target, onClose, onDeleted }: { target?: ProfileSummary; onClose: () => void; onDeleted?: () => void }) {
  const app = useApp();
  return (
    <Dialog open={target !== undefined} title={`删除「${target ? profileTitle(target) : ""}」？`}
      message="它的对话记录、记忆、技能和定时任务会从服务器上删除，无法恢复。分享给朋友的邀请也会失效。"
      onClose={onClose}
      buttons={[{ title: "取消", role: "cancel" }, {
        title: "删除", role: "destructive", onClick: () => {
          if (!target) return;
          app.deleteBot(target).then(() => onDeleted?.(), (e) => app.showToast(`删除失败：${e instanceof Error ? e.message : String(e)}`));
        },
      }]} />
  );
}

function BotDetail({ profile, onEdit, onClose, onDeleted }: { profile: ProfileSummary; onEdit: () => void; onClose: () => void; onDeleted: () => void }) {
  const app = useApp();
  const [stats, setStats] = useState<{ sessions: number; messages: number }>();
  const [pendingDelete, setPendingDelete] = useState<ProfileSummary>();
  const title = profileTitle(profile);
  useEffect(() => {
    if (app.isGuest || !app.api) return;
    insights(app.api, 7, app.profiles.apiName(profile)).then(setStats, () => {});
  }, [profile.name]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="form">
      <div className="bot-hero">
        <BotAvatar title={title} image={app.profiles.avatars.get(profile.name)} size={52} />
        <span className="col" style={{ gap: 2 }}>
          <span className="t-title3" style={{ fontWeight: 700 }}>{title}</span>
          <span className="t-caption t-2">{profile.model ?? "默认模型"}</span>
        </span>
      </div>
      <div className="form-card">
        <button className="list-button t-brand" onClick={() => { app.selectProfile(app.profiles.apiName(profile)); onClose(); }}>
          <MessagesSquare size={17} /> 和它对话
        </button>
      </div>
      {stats && (
        <div className="form-group">
          <span className="form-label">最近 7 天</span>
          <div className="form-card">
            <div className="value-line"><span>对话</span><span className="t-2">{stats.sessions}</span></div>
            <div className="value-line"><span>消息</span><span className="t-2">{stats.messages}</span></div>
          </div>
        </div>
      )}
      {!app.isGuest && Welcome.supportsSharing(app.hostWelcome) && <BotShareSections profile={profile} />}
      {!app.isGuest && (
        <div className="form-card">
          <button className="list-button" onClick={onEdit}><SlidersHorizontal size={17} /> 编辑<span className="spacer" /><ChevronRight size={14} className="t-3" /></button>
        </div>
      )}
      {!app.isGuest && !profile.is_default && (
        <div className="form-card"><button className="list-button t-danger" onClick={() => setPendingDelete(profile)}>删除机器人</button></div>
      )}
      <DeleteBotDialog target={pendingDelete} onClose={() => setPendingDelete(undefined)} onDeleted={onDeleted} />
    </div>
  );
}

const limitPresets: (number | undefined)[] = [undefined, 100_000, 500_000, 1_000_000, 3_000_000, 10_000_000];

/** 把一个 Bot 分享给朋友：生成邀请码，设每月 token 上限，管理已加入的访客（规则由主机上的 connector 执行） */
function BotShareSections({ profile }: { profile: ProfileSummary }) {
  const app = useApp();
  const [shares, setShares] = useState<BotShares>();
  const [error, setError] = useState<string>();
  const [creating, setCreating] = useState(false);
  const [guestToRemove, setGuestToRemove] = useState<ShareGuest>();
  const shareName = profile.is_default ? "default" : profile.name;
  const title = profileTitle(profile);

  const describe = (e: unknown) => e instanceof HermesError && e.kind === "http" && e.status === 404
    ? "主机上的 connector 版本太旧，不支持分享。在主机上运行 timibot-connector.py update 升级。"
    : e instanceof Error ? e.message : String(e);

  const load = async () => {
    if (!app.api) return;
    try { setShares(await botShares(app.api, shareName)); setError(undefined); } catch (e) { setError(describe(e)); }
  };
  useEffect(() => { void load(); }, [shareName]); // eslint-disable-line react-hooks/exhaustive-deps

  const newInvite = async () => {
    if (!app.api) return;
    setCreating(true);
    try {
      const invite = await createInvite(app.api, shareName, title);
      if (invite.short) { await copyText(RelayInviteCode.display(invite.short)); app.showToast("邀请码已复制，发给朋友就行"); }
      await load();
    } catch (e) { setError(describe(e)); } finally { setCreating(false); }
  };

  const saveLimit = async (value: string) => {
    if (!app.api || !shares) return;
    const tokens = value === "" ? undefined : Number(value);
    const previous = shares.usage;
    setShares({ ...shares, usage: { ...shares.usage, monthly_tokens: tokens } });
    try { const usage = await setMonthlyLimit(app.api, shareName, tokens); setShares((s) => s && { ...s, usage }); }
    catch (e) { setShares((s) => s && { ...s, usage: previous }); setError(describe(e)); }
  };

  const options = shares?.usage.monthly_tokens !== undefined && !limitPresets.includes(shares.usage.monthly_tokens)
    ? [...limitPresets, shares.usage.monthly_tokens] : limitPresets;

  return (
    <>
      <div className="form-group">
        <span className="form-label">分享给朋友</span>
        <div className="form-card">
          {shares?.invites.map((inv) => <InviteRow key={inv.id} invite={inv} botTitle={title} onRevoke={async () => { if (app.api) { await revokeInvite(app.api, inv.id).catch((e) => setError(describe(e))); await load(); } }} />)}
          <button className="list-button t-brand" disabled={creating} onClick={() => void newInvite()}>
            <Plus size={17} /> 新建邀请码<span className="spacer" />{creating && <Spinner size={14} />}
          </button>
        </div>
        <span className="t-caption t-2 form-hint">朋友在 TimiBot 配对页输入邀请码，就能使用「{title}」，不能改它的设置。一个码可以多人用，到期自动失效，右键（长按）可撤销。</span>
      </div>
      <div className="form-group">
        <span className="form-label">本月用量</span>
        <div className="form-card">
          {(shares?.credentials === "follow" || shares?.credentials === "main") && <div className="value-line"><span>API Key</span><span className="t-2">{shares.credentials === "main" ? "主账号" : "跟随主账号"}</span></div>}
          {/* 生图 / 生视频跟着 Key 一起跟随主账号（connector 1.10 起） */}
          {(shares?.credentials === "follow" || shares?.credentials === "main") && shares.media && shares.media !== "own" && (
            <div className="value-line"><span>生图与生视频</span><span className="t-2">{shares.media === "main" ? "主账号" : "跟随主账号"}</span></div>
          )}
          {shares?.credentials === "own" && (
            <button className="value-line" onClick={async () => {
              if (!app.api) return;
              try {
                const c = await followMainCredentials(app.api, shareName);
                setShares((s) => s && { ...s, credentials: c, media: s.media !== undefined ? c : undefined });
              } catch (e) { setError(describe(e)); }
            }}>
              <span>{shares.media === undefined ? "API Key" : "API Key 和生图"}</span><span className="t-brand">改为跟随主账号</span>
            </button>
          )}
          <div className="value-line"><span>已用</span><span className="t-2">{shares?.usage.used_tokens !== undefined ? `${TokenCount.short(shares.usage.used_tokens)} tokens` : "—"}</span></div>
          <label className="value-line">
            <span>每月上限</span>
            <select className="select" value={shares?.usage.monthly_tokens ?? ""} disabled={!shares} onChange={(e) => void saveLimit(e.target.value)}>
              {options.map((v) => <option key={v ?? "none"} value={v ?? ""}>{v !== undefined ? `${TokenCount.short(v)} tokens` : "不限"}</option>)}
            </select>
          </label>
        </div>
      </div>
      <div className="form-group">
        <span className="form-label">访客（{shares?.guests.length ?? 0}）</span>
        <div className="form-card">
          {shares?.guests.length ? shares.guests.map((g) => <GuestRow key={g.id} guest={g} onRemove={() => setGuestToRemove(g)} />)
            : <div className="value-line t-2">{shares ? "还没有人加入" : "加载中…"}</div>}
        </div>
      </div>
      {error && <div className="t-footnote t-danger">{error}</div>}
      <Dialog open={guestToRemove !== undefined} title="移除这位访客？" message="对方会立即断开，不能再使用这个 Bot。" onClose={() => setGuestToRemove(undefined)}
        buttons={[{ title: "取消", role: "cancel" }, { title: "移除", role: "destructive", onClick: async () => {
          if (!guestToRemove || !app.api) return;
          try { await removeGuest(app.api, guestToRemove.id, shareName); await load(); } catch (e) { setError(describe(e)); }
        } }]} />
    </>
  );
}

/** 一个有效的邀请码：点码或复制按钮复制，分享按钮直接发给朋友 */
function InviteRow({ invite, botTitle, onRevoke }: { invite: ShareInvite; botTitle: string; onRevoke: () => void }) {
  const [copied, setCopied] = useState(false);
  const menu = useMenu();
  const press = useLongPress((pos) => menu.show(pos));
  const code = invite.short ? RelayInviteCode.display(invite.short) : undefined;
  const copy = async () => { await copyText(code ?? invite.link); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  let expiry = "长期有效 · 可多人使用";
  if (invite.expires) {
    const days = Math.ceil((invite.expires * 1000 - Date.now()) / 86400000);
    expiry = (days <= 1 ? "今天到期" : `${days} 天后到期`) + " · 可多人使用";
  }
  const message = `我把 TimiBot 里的「${botTitle}」分享给你了。${code ? `打开 TimiBot（App 或网页版），在配对页输入邀请码 ${code}。` : ""}\n也可以直接粘贴：${invite.link}`;
  const share = async () => {
    if (navigator.share) { try { await navigator.share({ text: message }); return; } catch { return; } }
    await copyText(message);
    setCopied(true);
  };
  return (
    <div className="invite-row" {...press}>
      <button className="col invite-code" onClick={() => void copy()}>
        <span className="t-title3 mono semibold">{code ?? "邀请链接"}</span>
        <span className="t-caption" style={{ color: copied ? "var(--success)" : "var(--text-2)" }}>{copied ? "已复制" : expiry}</span>
      </button>
      <button className="t-brand" aria-label="复制邀请码" onClick={() => void copy()}><Copy size={18} /></button>
      <button className="t-brand" aria-label="发给朋友" onClick={() => void share()}><Share size={18} /></button>
      <Menu open={menu.open} anchor={menu.anchor} onClose={menu.close} align="start" width={160} items={[{ label: "撤销", icon: <Trash2 size={16} />, destructive: true, onSelect: onRevoke }]} />
    </div>
  );
}

function GuestRow({ guest, onRemove }: { guest: ShareGuest; onRemove: () => void }) {
  const menu = useMenu();
  const press = useLongPress((pos) => menu.show(pos));
  let seen = "在线";
  if (!guest.online) {
    const t = guest.last_seen ?? guest.added;
    if (t) {
      const days = Math.round((Date.now() / 1000 - t) / 86400);
      seen = days <= 0 ? "今天" : days === 1 ? "昨天" : `${days} 天前`;
    } else seen = "";
  }
  return (
    <div className="value-line" {...press}>
      <span className="row" style={{ gap: 8 }}><span className="status-dot" style={{ background: guest.online ? "var(--success)" : "var(--text-3)" }} />{guest.name ?? "iPhone"}</span>
      <span className="row" style={{ gap: 10 }}>
        <span className="t-caption t-2">{seen}</span>
        <button className="t-caption t-danger" onClick={onRemove}>移除</button>
      </span>
      <Menu open={menu.open} anchor={menu.anchor} onClose={menu.close} align="start" width={160} items={[{ label: "移除", icon: <Trash2 size={16} />, destructive: true, onSelect: onRemove }]} />
    </div>
  );
}

/** 编辑一个 Bot：名字（可以是中文）、简介、人设、默认模型。技能只读展示 */
function BotEdit({ profile }: { profile: ProfileSummary }) {
  const app = useApp();
  const [detail, setDetail] = useState<ProfileDetail>();
  const [title, setTitle] = useState(profileTitle(profile));
  const [soul, setSoul] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [showModels, setShowModels] = useState(false);

  const load = async () => {
    if (!app.api) return;
    try {
      const d = await app.api.describeProfile(profile.name);
      setDetail(d);
      setSoul(d.soul ?? "");
      setDescription(d.description ?? profile.description ?? "");
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };
  useEffect(() => { void load(); }, [profile.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!app.api) return;
    setSaving(true);
    try {
      await app.api.configureProfile(profile.name, description, soul);
      const t = title.trim();
      if (t && t !== profileTitle(profile)) await app.api.setBotTitle(profile.name, t, profile.botMeta);
      await app.profiles.refresh(app.api);
      app.showToast("已保存");
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setSaving(false); }
  };

  const skills = detail?.skills ?? [];
  return (
    <div className="form">
      <label className="form-group"><span className="form-label">名字</span><input className="form-field" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="机器人的名字" /></label>
      <label className="form-group"><span className="form-label">简介</span><textarea className="form-field" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="一句话介绍它" /></label>
      <div className="form-group">
        <span className="form-label">模型</span>
        <div className="form-card"><button className="value-line" onClick={() => setShowModels(true)}><span>默认模型</span><span className="t-2 row" style={{ gap: 6 }}>{detail?.model?.default ?? profile.model ?? "默认"}<ChevronRight size={14} className="t-3" /></span></button></div>
      </div>
      <label className="form-group"><span className="form-label">人设</span><textarea className="form-field mono t-footnote" rows={9} value={soul} onChange={(e) => setSoul(e.target.value)} /></label>
      {skills.length > 0 && (
        <div className="form-group">
          <span className="form-label">技能（{skills.filter((x) => x.enabled !== false).length}/{skills.length}）</span>
          <div className="form-card">
            {skills.map((x) => (
              <div key={x.name} className="value-line"><span className="t-subheadline">{x.name}</span>
                {x.enabled === false ? <Circle size={16} className="t-3" /> : <CircleCheck size={16} style={{ color: "var(--success)" }} />}</div>
            ))}
          </div>
        </div>
      )}
      {error && <div className="t-footnote t-danger">{error}</div>}
      <button className="primary-button" disabled={saving || !detail} onClick={() => void save()}>{saving ? "保存中…" : "保存"}</button>
      <ModelPickerSheet open={showModels} onClose={() => { setShowModels(false); void load(); }} bot={{ profile: app.profiles.apiName(profile) }} title="默认模型" />
    </div>
  );
}

/** Hermes 的 profile 名只能是小写字母、数字、- 和 _：中文名字时用 bot 加编号当 id，显示名另存 */
function makeBotID(title: string, taken: Set<string>): string {
  let base = title.toLowerCase().normalize("NFKD").replace(/[^a-z0-9]/g, "");
  if (!base || base === "default") base = "bot";
  base = base.slice(0, 24);
  let name = base;
  let n = 2;
  while (taken.has(name)) { name = `${base}${n}`; n += 1; }
  return name;
}

/** 新建机器人：只要一个名字（中文也行）。默认用主账号的模型和 API Key，人设和简介可以以后再写 */
function CreateBot({ open, onClose }: { open: boolean; onClose: () => void }) {
  const app = useApp();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [soul, setSoul] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  useEffect(() => { if (open) { setTitle(""); setDescription(""); setSoul(""); setError(undefined); } }, [open]);
  const trimmed = title.trim();
  const create = async () => {
    if (!app.api) return;
    setBusy(true);
    const name = makeBotID(trimmed, new Set(app.profiles.profiles.map((p) => p.name)));
    try {
      // 不从现有 Bot 复制：Hermes 会带上主账号的 Key 和模型，但不会带走你的记忆和对话
      await app.api.createProfile(name, description || undefined, soul || undefined, undefined);
      if (trimmed !== name) await app.api.setBotTitle(name, trimmed).catch(() => {});
      if (Welcome.supportsFollow(app.hostWelcome)) await followMainCredentials(app.api, name).catch(() => {});
      await app.profiles.refresh(app.api);
      onClose();
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };
  return (
    <Sheet open={open} onClose={onClose} title="新建机器人" leading={<button className="sheet-done" onClick={onClose}>取消</button>}
      trailing={<button className="sheet-done semibold" disabled={!trimmed || busy} onClick={() => void create()}>{busy ? "创建中…" : "创建"}</button>}>
      <div className="form">
        <label className="form-group"><span className="form-label">名字</span><input className="form-field" autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="例如 写作助手" /></label>
        <label className="form-group"><span className="form-label">简介</span><textarea className="form-field" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="一句话介绍它（可不填）" /></label>
        <label className="form-group"><span className="form-label">人设</span><textarea className="form-field" rows={6} value={soul} onChange={(e) => setSoul(e.target.value)} placeholder="身份、语气和工作方式（可不填）" /></label>
        {error && <div className="t-footnote t-danger">{error}</div>}
      </div>
    </Sheet>
  );
}
